import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import StorageIcon from "@mui/icons-material/Storage";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import AutoComplete from "../../components/common/AutoComplete";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import { ICustomButtonProps } from "../../components/common/CustomButton";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import CustomTable from "../../components/common/table/CustomTable";
import History from "../../components/History";
import ToastNotification from "../../components/ToastNotification";
import {
  addCurrencyApi,
  deleteCurrencyApi,
  editCurrencyApi,
  fetchAllCurrencies,
  fetchCurrency,
  fetchCurrencyHistory,
  ICurrency,
  useCurrenciesPageQuery,
} from "../../services/CurrencyService";
import {
  fetchAllCurrencyProviders,
  ICurrencyProvider,
} from "../../services/CurrencyProviderService";
import {
  createGlobalConfigApi,
  IGlobalConfig,
  useGlobalConfigByTypeQuery,
} from "../../services/SystemConfigService";
import { IField, Params } from "../../types/table/interfaces.type";
import { hasJwtPermission } from "../../services/ServicesHelpers";
import CurrencyProvidersDialog, {
  CurrencyProvidersDialogRef,
} from "./CurrencyProvidersDialog";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CurrencyDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => void;
}

export interface ReferenceCurrencyDialogRef {
  openDialog: (config: IGlobalConfig | null) => void;
}

interface ReferenceCurrencyDialogProps {
  onRefresh: () => void;
}

interface CurrencyDialogProps {
  onRefresh: () => void;
  appliedFilters: Params<ICurrency> & { _forceRefresh?: number };
  referenceCurrencyId?: number;
  referenceCurrencyName?: string;
}

interface DialogData extends Partial<ICurrency> {
  ratioToReferenceRaw?: string;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const CurrencyPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [appliedFilters, setAppliedFilters] = useState<
    Params<ICurrency> & { _forceRefresh?: number; _excludeId?: number }
  >({});

  const dialogRef = useRef<CurrencyDialogRef>(null);
  const refCurrencyDialogRef = useRef<ReferenceCurrencyDialogRef>(null);
  const providersDialogRef = useRef<CurrencyProvidersDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_CURRENCY"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        handleOpenDialog("delete", String(id));
      },
      isDisabled: !hasJwtPermission("DELETE_CURRENCY"),
    },
    onViewHistory: {
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_CURRENCIES_HISTORY"),
    },
  });

  // ── Reference Currency Banner ─────────────────────────────────────────────

  const { data: refCurrencyConfig } = useGlobalConfigByTypeQuery("REFERENCE_CURRENCY");

  const referenceCurrencyId = refCurrencyConfig?.value
    ? Number(refCurrencyConfig.value)
    : undefined;

  const { data: referenceCurrency } = useQuery({
    queryKey: ["referenceCurrency", referenceCurrencyId],
    queryFn: () => fetchCurrency(referenceCurrencyId!),
    enabled: !!referenceCurrencyId,
    staleTime: 5 * 60 * 1000,
  });

  const hasReferenceCurrency = !!referenceCurrencyId;

  const fields: IField<ICurrency>[] = [
    { key: "name", header: "Name" },
    {
      key: "ratioToReference",
      header: "Ratio to Reference",
      transform: (row) => {
        const value =
          row.ratioToReference !== undefined ? String(row.ratioToReference) : "-";
        if (row.ratioToReference === undefined || !referenceCurrency?.name) {
          return value;
        }
        return (
          <Tooltip
            title={`1 ${row.name} = ${row.ratioToReference} ${referenceCurrency.name}`}
            arrow
          >
            <span style={{ cursor: "default" }}>{value}</span>
          </Tooltip>
        );
      },
    },
    {
      key: "ratioUpdateMethod",
      header: "Update Method",
      transform: (row) => (
        <Chip
          label={row.ratioUpdateMethod}
          size="small"
          color={row.ratioUpdateMethod === "API" ? "primary" : "success"}
          clickable={false}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: "lastRatioUpdate",
      header: "Last Update",
      transform: (row) =>
        row.lastRatioUpdate
          ? new Date(row.lastRatioUpdate).toLocaleString()
          : "-",
    },
    { key: "createdBy", header: "Created By" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Currency"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />

      {/* Reference Currency Banner */}
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          borderColor: hasReferenceCurrency
            ? (COLORS.GOLD ?? "#b8860b")
            : "warning.main",
          backgroundColor: COLORS.WhiteBackground ?? "#fff",
        }}
      >
        {!hasReferenceCurrency ? (
          <Alert severity="warning" sx={{ alignItems: "center" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2">
                No reference currency is configured. All currency ratios require
                a reference currency to be meaningful.
              </Typography>
              {hasJwtPermission("POST_GLOBAL_CONFIG") && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={!hasJwtPermission("POST_GLOBAL_CONFIG")}
                  startIcon={<SettingsIcon fontSize="small" />}
                  onClick={() =>
                    refCurrencyDialogRef.current?.openDialog(null)
                  }
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Configure
                </Button>
              )}
            </Box>
          </Alert>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                Reference Currency
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {referenceCurrency?.name ?? `ID: ${referenceCurrencyId}`}
                </Typography>
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              All currency ratios are relative to this reference currency.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Currency List */}
      <CustomLabel
        text="Currency List"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <SearchHeaderContainer
        search={appliedFilters.search ?? ""}
        setSearch={(value) =>
          setAppliedFilters((prev) => ({ ...prev, search: value }))
        }
        buttonConfig={[
          {
            label: "Providers",
            size: "small",
            bgColor: COLORS.GOLD,
            startIcon: <StorageIcon />,
            onClick: () => providersDialogRef.current?.openDialog(),
            textColor: COLORS.iconColor,
            permission: "GET_ALL_CURRENCY_PROVIDERS",
          },
          {
            size: "small",
            bgColor: COLORS.GOLD,
            startIcon: <GridAddIcon />,
            onClick: () => handleOpenDialog("add"),
            textColor: COLORS.iconColor,
            permission: "POST_CURRENCY",
          },
        ]}
      />
      <CustomTable<ICurrency>
        filters={{ ...appliedFilters, _excludeId: referenceCurrencyId } as Params<ICurrency>}
        fields={fields}
        fetchFunc={useCurrenciesPageQuery}
        actions={actions}
        sortableFields={["name", "ratioToReference", "ratioUpdateMethod"]}
      />

      <CurrencyDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        appliedFilters={appliedFilters}
        referenceCurrencyId={referenceCurrencyId}
        referenceCurrencyName={referenceCurrency?.name}
      />

      <ReferenceCurrencyDialog
        ref={refCurrencyDialogRef}
        onRefresh={() => {
          queryClient.invalidateQueries({ queryKey: ["globalConfig", "REFERENCE_CURRENCY"] });
          setAppliedFilters((prev) => ({ ...prev, _forceRefresh: Date.now() }));
        }}
      />

      <CurrencyProvidersDialog
        ref={providersDialogRef}
        onChange={() => {
          queryClient.invalidateQueries({ queryKey: ["currenciesPage"] });
        }}
      />
    </Box>
  );
};

// ─── Reference Currency Configure Dialog ─────────────────────────────────────

const ReferenceCurrencyDialog = forwardRef<
  ReferenceCurrencyDialogRef,
  ReferenceCurrencyDialogProps
>(({ onRefresh }, ref) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | "">(
    ""
  );
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);

  useImperativeHandle(ref, () => ({
    openDialog: async (_config: IGlobalConfig | null) => {
      setSelectedCurrencyId("");
      try {
        const currencies = await fetchAllCurrencies();
        setCurrencyOptions(
          currencies.map((c) => ({ label: c.name, value: c.id }))
        );
      } catch {
        setCurrencyOptions([]);
      }
      setDialogOpen(true);
    },
  }));

  const handleSave = async () => {
    if (selectedCurrencyId === "") return;
    setIsSaving(true);
    try {
      await createGlobalConfigApi({
        type: "REFERENCE_CURRENCY",
        value: String(selectedCurrencyId),
      });
      ToastNotification.success("Reference currency configured successfully!");
      setDialogOpen(false);
      onRefresh();
    } catch (error: any) {
      ToastNotification.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const dialogFields = [
    {
      type: "custom" as const,
      component: (
        <AutoComplete
          label="Reference Currency"
          value={selectedCurrencyId}
          options={currencyOptions}
          onChange={(value) => {
            setSelectedCurrencyId(
              value !== "" && value !== null ? Number(value) : ""
            );
          }}
          clearable
          placeholder="Select Reference Currency"
          highlightLabelStar
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
        />
      ),
    },
  ];

  const dialogButtons: ICustomButtonProps[] = [
    {
      label: "Save",
      onClick: handleSave,
      bgColor: COLORS.GOLD,
      disabled: selectedCurrencyId === "" || isSaving,
    },
  ];

  return (
    <CustomDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      mode="edit"
      dialogData={{ accountType: "systemConfig" }}
      title="Set Reference Currency"
      fields={dialogFields}
      buttons={dialogButtons}
    />
  );
});

ReferenceCurrencyDialog.displayName = "ReferenceCurrencyDialog";

// ─── Currency Dialog ──────────────────────────────────────────────────────────

const CurrencyDialog = forwardRef<CurrencyDialogRef, CurrencyDialogProps>(
  ({ onRefresh, appliedFilters, referenceCurrencyId, referenceCurrencyName }, ref) => {
    const initialDialogData: DialogData = {
      name: "",
      ratioToReference: 0,
      ratioUpdateMethod: "MANUAL",
      currencyProviderId: null,
    };

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [dialogData, setDialogData] = useState<DialogData>(initialDialogData);
    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [providerOptions, setProviderOptions] = useState<
      { label: string; value: number }[]
    >([]);

    const isApiMode = dialogData.ratioUpdateMethod === "API";

    const loadProviders = async () => {
      try {
        const providers: ICurrencyProvider[] = await fetchAllCurrencyProviders();
        setProviderOptions(
          providers.map((p) => ({ label: p.name, value: p.id }))
        );
      } catch {
        setProviderOptions([]);
      }
    };

    useImperativeHandle(ref, () => ({
      openDialog: async (
        mode: "add" | "edit" | "delete" | "history",
        id?: string
      ) => {
        setDialogMode(mode);
        setSelectedId(id);
        setErrors({});

        try {
          if (
            (mode === "edit" || mode === "delete" || mode === "history") &&
            id
          ) {
            const entity = await fetchCurrency(Number(id));
            setDialogData({
              ...initialDialogData,
              ...entity,
            });
          } else {
            setDialogData(initialDialogData);
          }
          await loadProviders();
          setDialogOpen(true);
        } catch (error: any) {
          console.error("Error opening dialog:", error);
          ToastNotification.error("Failed to open the dialog.");
        }
      },
    }));

    useEffect(() => {
      let hasErrors = false;
      if (!dialogData.name?.trim()) hasErrors = true;
      if (!hasErrors && isApiMode && !dialogData.currencyProviderId) {
        hasErrors = true;
      }
      setIsSaveDisabled(hasErrors);
    }, [dialogData, isApiMode]);

    const buildPayload = (): Partial<ICurrency> => ({
      id: dialogData.id,
      name: dialogData.name,
      ratioToReference: dialogData.ratioToReference,
      ratioUpdateMethod: dialogData.ratioUpdateMethod,
      currencyProviderId: isApiMode ? dialogData.currencyProviderId : null,
      version: dialogData.version,
      createdBy: dialogData.createdBy,
      updatedBy: dialogData.updatedBy,
      createdDate: dialogData.createdDate,
      timestamp: dialogData.timestamp,
    });

    const handleSave = async () => {
      try {
        const payload = buildPayload();
        if (dialogMode === "edit" && selectedId) {
          await editCurrencyApi(selectedId, payload);
          ToastNotification.success("Currency updated successfully!");
        } else if (dialogMode === "add") {
          await addCurrencyApi(payload);
          ToastNotification.success("Currency added successfully!");
        }
        setDialogOpen(false);
        onRefresh();
        setDialogData(initialDialogData);
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const handleDelete = async () => {
      try {
        if (selectedId) {
          await deleteCurrencyApi(selectedId);
          ToastNotification.success("Currency deleted successfully!");
          setDialogOpen(false);
          onRefresh();
        }
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const getFields = () => {
      if (dialogMode === "history") {
        type FlatHistory = ICurrency & { historyTimestamp: string };

        const historyFields: IField<FlatHistory>[] = [
          { key: "name", header: "Name" },
          { key: "ratioToReference", header: "Ratio to Reference" },
          { key: "ratioUpdateMethod", header: "Update Method" },
          {
            key: "currencyProviderId",
            header: "Currency Provider",
            transform: (row) => {
              if (!row.currencyProviderId) return "-";
              return (
                providerOptions.find((p) => p.value === row.currencyProviderId)
                  ?.label ?? String(row.currencyProviderId)
              );
            },
          },
          { key: "lastRatioUpdate", header: "Last Ratio Update" },
          {
            key: "historyTimestamp" as keyof FlatHistory,
            header: "Date Modified",
          },
          { key: "createdBy", header: "Created By" },
          { key: "updatedBy", header: "Updated By" },
        ];

        return [
          {
            type: "custom" as const,
            component: (
              <History
                filters={appliedFilters as any}
                fields={historyFields as any}
                fetchFunc={({ size = 10 }: any) =>
                  fetchCurrencyHistory(Number(selectedId) || 0, { size })
                }
              />
            ),
          },
        ];
      }

      if (dialogMode === "delete") {
        return [
          {
            type: "custom" as const,
            component: (
              <Box>Are you sure you want to delete this Currency?</Box>
            ),
          },
        ];
      }

      return [
        {
          type: "text" as const,
          label: "Name",
          placeholder: "Currency Name (e.g. USD)",
          value: dialogData.name || "",
          error: errors.name || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          helperText: errors.name ? "Name is required." : "",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, name: String(value) }));
            setErrors((prev) => ({ ...prev, name: !value }));
          },
        },
        {
          type: "text" as const,
          label: "Ratio to Reference",
          placeholder: "e.g. 1.0",
          value:
            dialogData.ratioToReferenceRaw !== undefined
              ? dialogData.ratioToReferenceRaw
              : dialogData.ratioToReference !== undefined
              ? String(dialogData.ratioToReference)
              : "",
          error: errors.ratioToReference || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          helperText: errors.ratioToReference
            ? "Ratio to reference is required."
            : "",
          tooltip:
            dialogData.name && referenceCurrencyName
              ? `1 ${dialogData.name} = ${dialogData.ratioToReferenceRaw ?? dialogData.ratioToReference ?? ""} ${referenceCurrencyName}`
              : undefined,
          onChange: (value: string | number) => {
            const raw = String(value);
            const num = Number.parseFloat(raw);
            const invalid = Number.isNaN(num) || raw.trim() === "";
            setDialogData((prev) => ({
              ...prev,
              ratioToReferenceRaw: raw,
              ratioToReference: invalid ? 0 : num,
            }));
            setErrors((prev) => ({
              ...prev,
              ratioToReference: invalid,
            }));
          },
        },
        {
          type: "select" as const,
          label: "Update Method",
          placeholder: "Select Update Method",
          options: [
            { label: "Manual", value: "MANUAL" },
            { label: "API", value: "API" },
          ],
          value: dialogData.ratioUpdateMethod || "MANUAL",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              ratioUpdateMethod: String(value) as "MANUAL" | "API",
              ...(value !== "API" && { currencyProviderId: null }),
            }));
          },
        },
        ...(isApiMode
          ? [
              {
                type: "custom" as const,
                component: (
                  <AutoComplete
                    label="Currency Provider"
                    value={dialogData.currencyProviderId ?? ""}
                    options={providerOptions}
                    onChange={(value) => {
                      setDialogData((prev) => ({
                        ...prev,
                        currencyProviderId:
                          value !== "" && value !== null
                            ? Number(value)
                            : null,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        currencyProviderId: !value,
                      }));
                    }}
                    clearable
                    placeholder="Select Currency Provider"
                    highlightLabelStar
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: COLORS.WhiteBackground,
                      },
                    }}
                  />
                ),
              },
            ]
          : [
            {
              type: "text" as const,
              hidden: true,
              value: dialogData.currencyProviderId ?? "",
              onChange: (value: any) => {},
            },
          ]),
      ];
    };

    const isEditingReferenceCurrency =
      dialogMode === "edit" &&
      referenceCurrencyId !== undefined &&
      selectedId !== undefined &&
      Number(selectedId) === referenceCurrencyId;

    const dialogButtons: ICustomButtonProps[] = (() => {
      if (dialogMode === "history") return [];
      if (dialogMode === "delete") {
        return [
          {
            label: "Delete",
            onClick: handleDelete,
            variant: "contained" as const,
            bgColor: COLORS.GOLD,
            permission: "DELETE_CURRENCY",
          },
        ];
      }
      return [
        {
          label: "Finish",
          onClick: handleSave,
          bgColor: COLORS.GOLD,
          disabled:
            isSaveDisabled ||
            isEditingReferenceCurrency ||
            (dialogMode === "edit"
              ? !hasJwtPermission("PUT_CURRENCY")
              : !hasJwtPermission("POST_CURRENCY")),
        },
      ];
    })();

    const getTitle = () => {
      if (dialogMode === "history") return "Currency History";
      if (dialogMode === "delete") return "Delete Currency";
      if (dialogMode === "edit") return "Edit Currency";
      return "Add Currency";
    };

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setDialogData(initialDialogData);
        }}
        mode={dialogMode}
        dialogData={{ accountType: "currency" }}
        title={getTitle()}
        fields={getFields()}
        fieldsPerRow={dialogMode === "delete" ? undefined : 2}
        buttons={dialogButtons}
      />
    );
  }
);

CurrencyDialog.displayName = "CurrencyDialog";

export default CurrencyPage;
